import React from 'react';
import { useIntl } from 'react-intl';
import type { AssetSpecId } from 'fym-shared/src/music24/assetSpecs';
import type { ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import type { KitBannerSite, KitCard } from 'fym-shared/src/music24/story/kitCard';
import { TypedMemo } from '../../../../common/types';
import { kitVariantLabel } from '../../kitLabels';
import { CardPreview } from '../frames/CardPreview';
import { useElementWidth } from './useElementWidth';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

const GAP = 16;
/** A card is never drawn narrower than this; with less room the cards wrap. */
const MIN_CARD = 220;
/** One card (the thumbnail, the link preview) stops growing here. */
const MAX_SINGLE = 560;
/** The width before the first measure (and in the tests). */
const FALLBACK = 300;

const BANNER_SPECS: Record<KitBannerSite, AssetSpecId> = {
  youtube: 'bannerYoutube',
  x: 'bannerX',
  facebook: 'bannerFacebook',
};

/** The width of each card in a row of `count` cards in `room` px. Pure. */
export function wideCardWidth(room: number, count: number): number {
  if (room <= 0 || count <= 0) {
    return FALLBACK;
  }
  if (count === 1) {
    return Math.min(room, MAX_SINGLE);
  }
  const columns = Math.min(count, Math.max(1, Math.floor((room + GAP) / (MIN_CARD + GAP))));
  return Math.floor((room - (columns - 1) * GAP) / columns);
}

interface WideCardPreviewsProps {
  cards: KitCard[];
  look: ReleaseKitLook;
  palette: readonly string[];
  artworkUrl: string | undefined;
  watermark?: boolean;
}

/**
 * The kit cards in the full-width step 3 (an image with no choice): the cards side by side, as
 * large as the room allows, and they wrap on a narrow screen. Each banner has the name of its site
 * under it.
 */
const WideCardPreviewsComponent: React.FC<WideCardPreviewsProps> = ({
  cards,
  look,
  palette,
  artworkUrl,
  watermark,
}) => {
  const intl = useIntl();
  const [ref, room] = useElementWidth<HTMLDivElement>();
  const width = wideCardWidth(room, cards.length);
  return (
    <div ref={ref} className={styles.wideCards} data-testid="wide-card-previews">
      {cards.map((card) => (
        <figure key={card.kind === 'banner' ? card.site : card.kind} className={styles.wideCard}>
          <CardPreview
            card={card}
            look={look}
            palette={palette}
            artworkUrl={artworkUrl}
            watermark={watermark}
            width={width}
          />
          {card.kind === 'banner' && (
            <figcaption className={styles.wideCardLabel}>
              {kitVariantLabel(intl, { variant: card.site, specId: BANNER_SPECS[card.site] })}
            </figcaption>
          )}
        </figure>
      ))}
    </div>
  );
};

export const WideCardPreviews = TypedMemo(WideCardPreviewsComponent);
